import { Inject, Injectable, Optional } from '@angular/core';
import { Store } from '@ngrx/store';

import { Message, MessageType } from './message';
import { NOTIFICATIONS_CONFIG, NotificationsConfig } from './notifications-config';
import * as notificationStore from './store';
import * as notificationActions from './store/actions';
import * as notificationSelectors from './store/selectors';
import { take } from 'rxjs/operators';

@Injectable()
export class NotificationsService {
  public alerts$ = this.store.select(notificationSelectors.getAlerts);
  public toasts$ = this.store.select(notificationSelectors.getToasts);
  public systemAlerts$ = this.store.select(notificationSelectors.getSystemAlerts);

  options = {
    timeout: 5000,
    closeable: true,
    closeAfterRouteChange: false
  };

  addNotification(message: Message): Message {
    if (!message) {
      throw Error('A message must be defined!');
    }
    if (this.notificationsConfig) {
      this.options = {
        timeout: this.notificationsConfig.defaultTimeOut,
        closeable: this.notificationsConfig.closeable,
        closeAfterRouteChange: this.notificationsConfig.closeAfterRouteChange
      };
    }
    message = Object.assign(this.options, message);

    switch (message.type) {
      case MessageType.Alert:
        this.alerts$
          .pipe(
            take(1))
          .toPromise()
          .then((alerts: Message[]) => {
            const previousAlerts = alerts.filter(function (alert) {
              return alert.title === message.title && alert.message === message.message;
            });
            if (!previousAlerts || previousAlerts.length === 0) {
              this.store.dispatch(new notificationActions.MessageActions.AddMessage(message));
            }
          });
        break;
      case MessageType.Toast:
        this.toasts$
          .pipe(
            take(1))
          .toPromise()
          .then((toasts: Message[]) => {
            const previousToasts = toasts.filter(function (toast) {
              return toast.title === message.title && toast.message === message.message;
            });
            if (!previousToasts || previousToasts.length === 0) {
              this.store.dispatch(new notificationActions.MessageActions.AddMessage(message));
            }
          });
        break;
      case MessageType.SystemAlert:
        this.systemAlerts$
          .pipe(
            take(1))
          .toPromise().
          then((alerts: Message[]) => {
            const previousAlerts = alerts.filter(function (alert) {
              return alert.title === message.title && alert.message === message.message;
            });
            if (!previousAlerts || previousAlerts.length === 0) {
              this.store.dispatch(new notificationActions.MessageActions.AddMessage(message));
            }
          });
        break;
      default:
        this.store.dispatch(new notificationActions.MessageActions.AddMessage(message));
    }
    return message;
  }

  removeNotification(message: Message) {
    this.store.dispatch(new notificationActions.MessageActions.RemoveMessage(message));
  }

  clearNotifications(type: MessageType) {
    this.store.dispatch(new notificationActions.MessageActions.ClearMessages(type));
  }

  constructor(
    private store: Store<notificationStore.State>,
    @Optional()
    @Inject(NOTIFICATIONS_CONFIG)
    private notificationsConfig: NotificationsConfig
  ) { }
}
